import { Controller, useFormContext } from 'react-hook-form';
import type { MultiValue, SingleValue } from 'react-select';

import type { CompoundType } from '@polyai/common/types/helpers';
import type {
  OptionType,
  SelectProps,
} from '@polyai/ui/components/atoms/Select';

import * as Styled from './InputStyles.styled';
import type { SharedFormProps } from './types';

type Props<T, S extends OptionType> = CompoundType<
  SharedFormProps<T> & SelectProps<S>
> & {
  transformSingleValue?: (value: SingleValue<S>) => any;
  transformMultiValue?: (value: MultiValue<S>) => any;
  emptyValue?: string;
};

const noTransform = (v: any) => v as any;

export const FormSelect = <T, S extends OptionType = any>({
  controlKey,
  portal = false,
  onChange: onChangeProp,
  transformSingleValue = noTransform,
  transformMultiValue = noTransform,
  errorIcon,
  emptyValue,
  ...rest
}: Props<T, S>) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={controlKey as string}
      render={({ field, fieldState: { error } }) => {
        return (
          <Styled.FormSelect
            {...field}
            value={
              emptyValue !== undefined
                ? field.value?.value === emptyValue
                  ? null
                  : field.value
                : field.value
            }
            errorIcon={errorIcon}
            errors={!!error ? [error.message || ''] : []}
            portal={portal}
            onChange={(newValue: unknown, actionMeta: unknown) => {
              field.onChange(
                rest.isMulti
                  ? transformMultiValue(newValue as MultiValue<S>)
                  : transformSingleValue(newValue as SingleValue<S>),
              );

              // @ts-ignore - allow passing through the Select onChange signature
              onChangeProp?.(newValue, actionMeta);
            }}
            {...rest}
          />
        );
      }}
    />
  );
};
